import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { appointmentService, lawyerService, realtimeChatService } from '../services/firebaseService.js';
import LawyerDashboardMetrics from './LawyerDashboardMetrics';
import { 
  Calendar, 
  Users, 
  MessageSquare, 
  DollarSign, 
  TrendingUp, 
  FileText,
  Clock,
  Star,
  Settings,
  Scale,
  Wifi,
  WifiOff,
  X,
  Clock as ClockIcon,
  Calendar as CalendarIcon,
  MapPin,
  Home,
  Building,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { useEffect } from 'react';
import toast from 'react-hot-toast';

const LawyerDashboard = () => {
  const navigate = useNavigate();
  const { language, user, clearStorageSpace } = useApp();
  const [realData, setRealData] = useState({
    totalClients: 0,
    totalEarnings: 0,
    pendingCases: 0,
    totalMessages: 0,
    availableBalance: 0
  });
  
  const [consultationTypes, setConsultationTypes] = useState({
    'Video Call': { count: 0, totalAmount: 0 },
    'Phone Call': { count: 0, totalAmount: 0 },
    'In Person': { count: 0, totalAmount: 0 }
  });
  
  const [recentCases, setRecentCases] = useState([]);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [walletData, setWalletData] = useState({
    availableBalance: 0,
    pendingBalance: 0,
    totalEarnings: 0
  });
  const [lawyerName, setLawyerName] = useState('Lawyer');
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  
  // Online/Offline status state
  const [isOnline, setIsOnline] = useState(true);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [offlineReason, setOfflineReason] = useState('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [showReturnTimeModal, setShowReturnTimeModal] = useState(false);
  const [selectedReturnTime, setSelectedReturnTime] = useState('');
  const [customReturnDateTime, setCustomReturnDateTime] = useState('');
  const [showCustomDateTimePicker, setShowCustomDateTimePicker] = useState(false);
  
  // Schedule Time Off states
  const [showScheduleReasonModal, setShowScheduleReasonModal] = useState(false);
  const [scheduleReason, setScheduleReason] = useState('');
  const [showScheduleDateTimeModal, setShowScheduleDateTimeModal] = useState(false);
  const [scheduleStartDate, setScheduleStartDate] = useState('');
  const [scheduleStartTime, setScheduleStartTime] = useState('');
  const [scheduleEndDate, setScheduleEndDate] = useState('');
  const [scheduleEndTime, setScheduleEndTime] = useState('');

  const translations = {
    english: {
      welcome: 'Welcome back,',
      toNyaySahaya: 'to NyaySahaya',
      dashboard: 'Lawyer Dashboard',
      overview: 'Overview',
      todayAppointments: 'Today\'s Appointments',
      totalClients: 'Total Clients',
      totalEarnings: 'Total Earnings',
      pendingCases: 'Pending Cases',
      recentActivity: 'Recent Activity',
      upcomingAppointments: 'Upcoming Appointments',
      noAppointments: 'No appointments scheduled for today',
      viewAll: 'View All',
      quickActions: 'Quick Actions',
      rescheduleAppointment: 'Reschedule Appointment',
      viewCases: 'View Cases',
      manageProfile: 'Manage Profile',
      earnings: 'Earnings',
      clients: 'Clients',
      cases: 'Cases',
      messages: 'Messages',
      availableBalance: 'Available Balance',
      activeStatus: 'Active Status',
      online: 'Online',
      offline: 'Offline',
      goOfflineNow: 'Go Offline Now',
      scheduleTimeOff: 'Schedule Time Off',
      offlineConfirmation: 'Do you want to go offline?',
      cancel: 'Cancel',
      reasonForOffline: 'Reason for going offline',
      incorrectAvailability: 'Incorrect availability timing',
      temporarilyClose: 'Temporarily close',
      whenWillYouReturn: 'When would you come back?',
      thirtyMinutes: '30 min',
      oneHour: '1 hr',
      twoHours: '2 hr',
      untilTurnOn: 'Until I turn myself on',
      tomorrow: 'Tomorrow',
      openingTime: 'Opening time',
             specifyDateTime: 'Specify date and time',
       selectDateTime: 'Select date and time',
       reasonForScheduling: 'Reason for scheduling time off',
       closedDueToFestival: 'Closed due to festival',
       permanentlyShut: 'Permanently shut',
       goingOutOfStation: 'Going out of station',
       others: 'Others',
       startDate: 'Start Date',
       startTime: 'Start Time',
       endDate: 'End Date',
       endTime: 'End Time',
       scheduleOffline: 'Schedule Offline'
    },
    hindi: {
      welcome: 'वापसी पर स्वागत है,',
      toNyaySahaya: 'NyaySahaya में',
      dashboard: 'वकील डैशबोर्ड',
      overview: 'अवलोकन',
      todayAppointments: 'आज की नियुक्तियां',
      totalClients: 'कुल ग्राहक',
      totalEarnings: 'कुल कमाई',
      pendingCases: 'लंबित मामले',
      recentActivity: 'हाल की गतिविधि',
      upcomingAppointments: 'आगामी नियुक्तियां',
      noAppointments: 'आज के लिए कोई नियुक्ति निर्धारित नहीं है',
      viewAll: 'सभी देखें',
      quickActions: 'त्वरित कार्य',
      rescheduleAppointment: 'नियुक्ति पुनर्निर्धारित करें',
      viewCases: 'मामले देखें',
      manageProfile: 'प्रोफाइल प्रबंधित करें',
      earnings: 'कमाई',
      clients: 'ग्राहक',
      cases: 'मामले',
      messages: 'संदेश',
      availableBalance: 'उपलब्ध बैलेंस',
      activeStatus: 'सक्रिय स्थिति',
      online: 'ऑनलाइन',
      offline: 'ऑफलाइन',
      goOfflineNow: 'अभी ऑफलाइन जाएं',
      scheduleTimeOff: 'समय बंद करें',
      offlineConfirmation: 'क्या आप ऑफलाइन जाना चाहते हैं?',
      cancel: 'रद्द करें',
      reasonForOffline: 'ऑफलाइन जाने का कारण',
      incorrectAvailability: 'गलत उपलब्धता समय',
      temporarilyClose: 'अस्थायी रूप से बंद करें',
      whenWillYouReturn: 'आप कब वापस आएंगे?',
      thirtyMinutes: '30 मिनट',
      oneHour: '1 घंटा',
      twoHours: '2 घंटे',
      untilTurnOn: 'जब तक मैं खुद को चालू न करूं',
      tomorrow: 'कल',
      openingTime: 'खुलने का समय',
             specifyDateTime: 'दिनांक और समय निर्दिष्ट करें',
       selectDateTime: 'दिनांक और समय चुनें',
       reasonForScheduling: 'समय बंद करने का कारण',
       closedDueToFestival: 'त्योहार के कारण बंद',
       permanentlyShut: 'स्थायी रूप से बंद',
       goingOutOfStation: 'स्टेशन से बाहर जा रहे हैं',
       others: 'अन्य',
       startDate: 'प्रारंभ तिथि',
       startTime: 'प्रारंभ समय',
       endDate: 'समाप्ति तिथि',
       endTime: 'समाप्ति समय',
       scheduleOffline: 'ऑफलाइन शेड्यूल करें'
    }
  };

  const t = translations[language] || translations.english;

  // Calculate real data from cases and appointments
  const calculateRealData = () => {
    try {
      const lawyerCases = JSON.parse(localStorage.getItem('lawyer_cases') || '[]');
      const lawyerAppointments = JSON.parse(localStorage.getItem('lawyer_appointments') || '[]');
      const lawyerChats = JSON.parse(localStorage.getItem('lawyer_chats') || '[]');

      console.log('Raw localStorage data:', {
        lawyerCases,
        lawyerAppointments,
        lawyerChats
      });

      // Ensure we have arrays, not objects or other data types
      const casesArray = Array.isArray(lawyerCases) ? lawyerCases : [];
      const appointmentsArray = Array.isArray(lawyerAppointments) ? lawyerAppointments : [];
      const chatsArray = Array.isArray(lawyerChats) ? lawyerChats : [];

      console.log('Processed arrays:', {
        casesArray: casesArray.length,
        appointmentsArray: appointmentsArray.length,
        chatsArray: chatsArray.length
      });

      // Calculate total clients (unique clients from all cases and appointments)
      const allClients = new Set();
      
      // Add clients from cases
      casesArray.forEach(caseItem => {
        if (caseItem && caseItem.clientName) {
          allClients.add(caseItem.clientName);
        }
      });
      
      // Add clients from appointments
      appointmentsArray.forEach(apt => {
        if (apt && apt.clientName) {
          allClients.add(apt.clientName);
        }
      });

      // Calculate total earnings
      let totalEarnings = 0;
      
      // Calculate from cases
      casesArray.forEach(caseItem => {
        if (caseItem && caseItem.amount && caseItem.status === 'completed') {
          const amount = parseInt(caseItem.amount.toString().replace('₹', '').replace(',', ''));
          if (!isNaN(amount)) {
            totalEarnings += amount;
          }
        }
      });
      
      // Calculate from appointments
      appointmentsArray.forEach(apt => {
        if (apt && apt.amount && apt.status === 'completed') {
          const amount = parseInt(apt.amount.toString().replace('₹', '').replace(',', ''));
          if (!isNaN(amount)) {
            totalEarnings += amount;
          }
        }
      });

      // Calculate pending cases
      let pendingCases = 0;
      
      // Count from cases
      casesArray.forEach(caseItem => {
        if (caseItem && caseItem.status === 'pending') {
          pendingCases++;
        }
      });
      
      // Count from appointments
      appointmentsArray.forEach(apt => {
        if (apt && apt.status === 'pending') {
          pendingCases++;
        }
      });

      // Calculate total messages (from chats)
      let totalMessages = 0;
      chatsArray.forEach(chat => {
        if (chat && chat.unreadCount) {
          totalMessages += chat.unreadCount;
        }
      });

      // Get available balance from multiple sources - use the highest value
      let availableBalance = walletData.availableBalance || 0;
      console.log('🔍 calculateRealData - Starting with walletData.availableBalance:', availableBalance);
      
      // Also check dashboard metrics if available
      if (dashboardMetrics?.availableBalance && dashboardMetrics.availableBalance > availableBalance) {
        availableBalance = dashboardMetrics.availableBalance;
        console.log('💰 Using higher balance from dashboard metrics:', availableBalance);
      } else {
        console.log('🔍 dashboardMetrics check - availableBalance:', dashboardMetrics?.availableBalance, 'current availableBalance:', availableBalance);
      }
      
      // Also check localStorage for wallet data
      try {
        const localStorageWallet = JSON.parse(localStorage.getItem('lawyer_wallet') || '{}');
        console.log('🔍 localStorage wallet data:', localStorageWallet);
        if (localStorageWallet.availableBalance && parseFloat(localStorageWallet.availableBalance) > availableBalance) {
          availableBalance = parseFloat(localStorageWallet.availableBalance);
          console.log('💰 Using higher balance from localStorage:', availableBalance);
        }
      } catch (error) {
        console.log('Error checking localStorage wallet:', error);
      }
      
      console.log('💰 Final available balance calculated in calculateRealData:', availableBalance);

      const result = {
        totalClients: allClients.size,
        totalEarnings: totalEarnings,
        pendingCases: pendingCases,
        totalMessages: totalMessages,
        availableBalance: availableBalance
      };

      console.log('Final calculated result:', result);
      return result;
    } catch (error) {
      console.error('Error calculating real data:', error);
      // Return default values if there's an error
      return {
        totalClients: 0,
        totalEarnings: 0,
        pendingCases: 0,
        totalMessages: 0,
        availableBalance: walletData.availableBalance || 0
      };
    }
  };

  // Load pending appointments
  const loadPendingAppointments = async () => {
    if (!user?.id) return;
    
    try {
      setLoadingPending(true);
      const result = await appointmentService.getPendingAppointments(user.id);
      
      if (result.success) {
        setPendingAppointments(result.data || []);
      } else {
        console.error('Failed to load pending appointments:', result.error);
      }
    } catch (error) {
      console.error('Error loading pending appointments:', error);
    } finally {
      setLoadingPending(false);
    }
  };

  const loadRecentCases = async () => {
    if (!user?.id) return;
    
    try {
      const result = await appointmentService.getLawyerAppointments(user.id);
      if (result.success) {
        // Get recent cases (last 5 appointments)
        const recentCases = (result.data || [])
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);
        setRecentCases(recentCases);
      } else {
        console.error('Failed to load recent cases:', result.error);
      }
    } catch (error) {
      console.error('Error loading recent cases:', error);
    }
  };

  // Load lawyer profile data to get the name
  const loadLawyerProfile = async () => {
    if (!user?.id) return;
    
    try {
      // Try to get lawyer profile from localStorage first
      const lawyerProfile = JSON.parse(localStorage.getItem('lawyer_profile') || '{}');
      if (lawyerProfile.fullName) {
        setLawyerName(lawyerProfile.fullName);
        console.log('👤 Lawyer name loaded from profile:', lawyerProfile.fullName);
        return;
      }
      
      // If not in localStorage, try to get from Firestore
      const { default: firebaseService } = await import('../services/firebaseService');
      const profileResult = await firebaseService.getLawyerByUserId(user.id);
      if (profileResult.success && profileResult.data?.full_name) {
        // Store the profile in localStorage for future use
        localStorage.setItem('lawyer_profile', JSON.stringify(profileResult.data));
        setLawyerName(profileResult.data.full_name);
        console.log('👤 Lawyer name loaded from Firestore and stored in localStorage:', profileResult.data.full_name);
      }
    } catch (error) {
      console.error('Error loading lawyer profile:', error);
    }
  };

  // Load dashboard metrics to get lawyer name and other data
  const loadDashboardMetrics = async () => {
    if (!user?.id) return;
    
    try {
      const { default: dashboardService } = await import('../services/dashboardService');
      const result = await dashboardService.calculateLawyerDashboardMetrics(user.id);
      
      if (result.success) {
        // Set dashboard metrics first
        setDashboardMetrics(result.data);
        
        if (result.data?.lawyerName) {
          setLawyerName(result.data.lawyerName);
        }
        
        if (result.data?.availableBalance) {
          // Sync the available balance from dashboard metrics
          const balance = result.data.availableBalance;
          
          // Always override the balance from dashboard metrics (this is the source of truth)
          setRealData(prev => ({
            ...prev,
            availableBalance: balance
          }));
          
          // Also update wallet data state to keep them in sync
          setWalletData(prev => ({
            ...prev,
            availableBalance: balance
          }));
        }
      }
    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
    }
  };

  // Load wallet data
  const loadWalletData = async () => {
    if (!user?.id) return Promise.resolve();
    
    try {
      // Import wallet service dynamically
      const walletService = await import('../services/walletService');
      console.log('💰 loadWalletData - service imported:', walletService);
      
      const result = await walletService.default.getLawyerWallet(user.id);
      if (result.success) {
        const wallet = result.data;
        const balance = wallet.availableBalance || 0;
        
        console.log('💰 Raw wallet data received:', wallet);
        console.log('💰 Available balance extracted:', balance);
        
        setWalletData({
          availableBalance: balance,
          pendingBalance: wallet.pendingBalance || 0,
          totalEarnings: wallet.totalEarnings || 0
        });
        
        // Also update realData with wallet information
        setRealData(prev => ({
          ...prev,
          availableBalance: balance
        }));
        
        console.log('💰 Wallet data loaded and state updated:', { balance, walletData: wallet });
        return Promise.resolve();
      } else {
        console.error('Failed to load wallet data:', result.error);
        
              // Fallback: Try to get wallet data from localStorage
      try {
        // Check multiple possible localStorage keys
        const localStorageWallet = JSON.parse(localStorage.getItem('lawyer_wallet') || '{}');
        const localStorageBalance = JSON.parse(localStorage.getItem('lawyer_balance') || '{}');
        const localStorageEarnings = JSON.parse(localStorage.getItem('lawyer_earnings') || '{}');
        
        console.log('💰 Checking localStorage for wallet data:', {
          lawyer_wallet: localStorageWallet,
          lawyer_balance: localStorageBalance,
          lawyer_earnings: localStorageEarnings
        });
        
        let fallbackBalance = 0;
        
        if (localStorageWallet.availableBalance) {
          fallbackBalance = parseFloat(localStorageWallet.availableBalance) || 0;
        } else if (localStorageBalance.balance) {
          fallbackBalance = parseFloat(localStorageBalance.balance) || 0;
        } else if (localStorageEarnings.availableBalance) {
          fallbackBalance = parseFloat(localStorageEarnings.availableBalance) || 0;
        }
        
        if (fallbackBalance > 0) {
          console.log('💰 Using fallback localStorage wallet data:', fallbackBalance);
          
          setWalletData({
            availableBalance: fallbackBalance,
            pendingBalance: localStorageWallet.pendingBalance || 0,
            totalEarnings: localStorageWallet.totalEarnings || 0
          });
          
          setRealData(prev => ({
            ...prev,
            availableBalance: fallbackBalance
          }));
        } else {
          console.log('💰 No fallback wallet data found in localStorage');
        }
      } catch (fallbackError) {
        console.error('Fallback wallet data also failed:', fallbackError);
      }
        
        return Promise.resolve();
      }
    } catch (error) {
      console.error('Error loading wallet data:', error);
      return Promise.resolve();
    }
  };

  // Handle accept appointment
  const handleAcceptAppointment = async (appointmentId) => {
    try {
      const result = await appointmentService.acceptAppointment(appointmentId, user.id);
      
      if (result.success) {
        toast.success('Appointment accepted successfully!');
        loadPendingAppointments(); // Reload pending appointments
        // Also reload dashboard data
        const newData = await calculateRealData();
        setRealData(newData);
      } else {
        toast.error('Failed to accept appointment');
      }
    } catch (error) {
      console.error('Error accepting appointment:', error);
      toast.error('Error accepting appointment');
    }
  };

  // Handle reject appointment
  const handleRejectAppointment = async (appointmentId, reason = '') => {
    try {
      const result = await appointmentService.rejectAppointment(appointmentId, user.id, reason);
      
      if (result.success) {
        toast.success('Appointment rejected');
        loadPendingAppointments(); // Reload pending appointments
        // Also reload dashboard data
        const newData = await calculateRealData();
        setRealData(newData);
      } else {
        toast.error('Failed to reject appointment');
      }
    } catch (error) {
      console.error('Error rejecting appointment:', error);
      toast.error('Error rejecting appointment');
    }
  };

  // Calculate consultation types data
  const calculateConsultationTypes = () => {
    try {
      const lawyerCases = JSON.parse(localStorage.getItem('lawyer_cases') || '[]');
      const lawyerAppointments = JSON.parse(localStorage.getItem('lawyer_appointments') || '[]');
      
      const casesArray = Array.isArray(lawyerCases) ? lawyerCases : [];
      const appointmentsArray = Array.isArray(lawyerAppointments) ? lawyerAppointments : [];
      
      // Combine cases and appointments
      const allConsultations = [...casesArray, ...appointmentsArray];
      
      // Count by consultation type
      const consultationStats = {
        'Video Call': { count: 0, totalAmount: 0 },
        'Phone Call': { count: 0, totalAmount: 0 },
        'In Person': { count: 0, totalAmount: 0 }
      };
      
      allConsultations.forEach(consultation => {
        if (consultation && consultation.type) {
          const type = consultation.type;
          if (consultationStats[type]) {
            consultationStats[type].count++;
            if (consultation.amount) {
              const amount = parseInt(consultation.amount.toString().replace('₹', '').replace(',', ''));
              if (!isNaN(amount)) {
                consultationStats[type].totalAmount += amount;
              }
            }
          }
        }
      });
      
      return consultationStats;
    } catch (error) {
      console.error('Error calculating consultation types:', error);
      return {
        'Video Call': { count: 0, totalAmount: 0 },
        'Phone Call': { count: 0, totalAmount: 0 },
        'In Person': { count: 0, totalAmount: 0 }
      };
    }
  };

  // Get recent cases data
  const getRecentCases = () => {
    try {
      const lawyerCases = JSON.parse(localStorage.getItem('lawyer_cases') || '[]');
      const lawyerAppointments = JSON.parse(localStorage.getItem('lawyer_appointments') || '[]');
      
      const casesArray = Array.isArray(lawyerCases) ? lawyerCases : [];
      const appointmentsArray = Array.isArray(lawyerAppointments) ? lawyerAppointments : [];
      
      // Combine and sort by date (most recent first)
      const allCases = [...casesArray, ...appointmentsArray]
        .filter(item => item && item.clientName) // Filter out invalid items
        .sort((a, b) => {
          // Sort by date, most recent first
          const dateA = a.dateTime || a.date || '';
          const dateB = b.dateTime || b.date || '';
          return dateB.localeCompare(dateA);
        })
        .slice(0, 3); // Get only the 3 most recent
      
      return allCases;
    } catch (error) {
      console.error('Error getting recent cases:', error);
      return [];
    }
  };

  // Load initial availability status and set up real-time subscriptions
  useEffect(() => {
    if (user?.id) {
      loadCurrentAvailabilityStatus();
      loadPendingAppointments();
      loadRecentCases();
      loadLawyerProfile(); // Load lawyer profile for name
      loadDashboardMetrics(); // Load dashboard metrics for lawyer name
      
      // Load wallet data first, then calculate real data
      loadWalletData().then(() => {
        // After wallet data is loaded, calculate real data
        const data = calculateRealData();
        setRealData(data);
      });
      
      // Also try direct wallet sync
      syncWalletBalance();
      
      // CRITICAL FIX: Load dashboard metrics LAST with a longer delay
      // This ensures it overrides any incorrect values from other sources
      setTimeout(() => {
        console.log('🚀 Loading dashboard metrics as final step...');
        loadDashboardMetrics();
      }, 2000); // Longer delay to ensure wallet data is fully processed

      // Set up real-time availability subscription
      const availabilityChannel = realtimeChatService.subscribeToLawyerAvailability(
        user.id,
        (updatedProfile) => {
          const { availability_status, offline_reason } = updatedProfile;
          setIsOnline(availability_status === 'online');
          setOfflineReason(offline_reason || '');
        }
      );

      // Set up real-time dashboard metrics subscription
      const metricsChannel = realtimeChatService.subscribeToDashboardMetrics(
        user.id,
        (payload) => {
          // Refresh dashboard data when appointments or messages change
          loadPendingAppointments();
          calculateRealData().then(data => {
            setRealData(data);
          });
        }
      );

      // Set up real-time booking notifications
      const bookingNotificationChannel = realtimeChatService.subscribeToAppointmentStatus(
        user.id,
        (payload) => {
          if (payload.eventType === 'INSERT') {
            // New booking notification
            toast.success('New booking request received!', {
              duration: 5000,
              position: 'top-right'
            });
            loadPendingAppointments();
            calculateRealData().then(data => {
              setRealData(data);
            });
          } else if (payload.eventType === 'UPDATE') {
            const newRecord = payload.new;
            const oldRecord = payload.old;
            
            if (newRecord.status !== oldRecord.status) {
              // Booking status change notification
              toast.success(`Booking ${newRecord.status}!`, {
                duration: 4000,
                position: 'top-right'
              });
              loadPendingAppointments();
              calculateRealData().then(data => {
                setRealData(data);
              });
            }
          }
        }
      );

      // Set up real-time case status tracking
      const caseStatusChannel = realtimeChatService.subscribeToCaseStatusUpdates(
        user.id,
        (payload) => {
          const newRecord = payload.new;
          const oldRecord = payload.old;
          
          if (newRecord.status !== oldRecord.status) {
            // Case status change notification
            const statusMessages = {
              'confirmed': 'Case confirmed by client',
              'rejected': 'Case rejected by client',
              'cancelled': 'Case cancelled',
              'completed': 'Case completed',
              'auto_cancelled': 'Case auto-cancelled due to timeout'
            };
            
            const message = statusMessages[newRecord.status] || `Case status changed to ${newRecord.status}`;
            toast.success(message, {
              duration: 4000,
              position: 'top-right'
            });
            
            // Refresh dashboard data
            loadPendingAppointments();
            calculateRealData().then(data => {
              setRealData(data);
            });
          }
        }
      );

      // Set up real-time case activity tracking
      const caseActivityChannel = realtimeChatService.subscribeToCaseActivity(
        user.id,
        (payload) => {
          // Refresh recent cases when there's any activity
          loadRecentCases();
        }
      );

      // NEW: Set up comprehensive real-time subscriptions for all dashboard metrics
      
      // Real-time earnings updates
      const earningsChannel = realtimeChatService.subscribeToEarningsUpdates(
        user.id,
        (payload) => {
          console.log('💰 Real-time earnings update received:', payload);
          calculateRealData().then(data => {
            setRealData(prev => ({ ...prev, ...data }));
          });
          // Also update consultation types
          const newConsultationTypes = calculateConsultationTypes();
          setConsultationTypes(newConsultationTypes);
        }
      );

      // Real-time client updates
      const clientsChannel = realtimeChatService.subscribeToClientUpdates(
        user.id,
        (payload) => {
          console.log('👥 Real-time client update received:', payload);
          calculateRealData().then(data => {
            setRealData(prev => ({ ...prev, ...data }));
          });
        }
      );

      // Real-time case status updates (pending cases)
      const pendingCasesChannel = realtimeChatService.subscribeToPendingCaseUpdates(
        user.id,
        (payload) => {
          console.log('📋 Real-time pending case update received:', payload);
          calculateRealData().then(data => {
            setRealData(prev => ({ ...prev, ...data }));
          });
          // Also update recent cases
          loadRecentCases();
        }
      );

      // Real-time message updates
      const messagesChannel = realtimeChatService.subscribeToMessageUpdates(
        user.id,
        (payload) => {
          console.log('💬 Real-time message update received:', payload);
          calculateRealData().then(data => {
            setRealData(prev => ({ ...prev, ...data }));
          });
        }
      );

      // Real-time consultation type updates
      const consultationTypesChannel = realtimeChatService.subscribeToConsultationTypeUpdates(
        user.id,
        (payload) => {
          console.log('🎯 Real-time consultation type update received:', payload);
          const newConsultationTypes = calculateConsultationTypes();
          setConsultationTypes(newConsultationTypes);
        }
      );

      // Real-time appointment updates
      const appointmentsChannel = realtimeChatService.subscribeToAppointmentUpdates(
        user.id,
        (payload) => {
          console.log('📅 Real-time appointment update received:', payload);
          loadPendingAppointments();
          calculateRealData().then(data => {
            setRealData(prev => ({ ...prev, ...data }));
          });
          // Also update consultation types
          const newConsultationTypes = calculateConsultationTypes();
          setConsultationTypes(newConsultationTypes);
        }
      );

      // Real-time performance stats updates
      const performanceChannel = realtimeChatService.subscribeToPerformanceUpdates(
        user.id,
        (payload) => {
          console.log('📊 Real-time performance update received:', payload);
          // Refresh all dashboard data
          calculateRealData().then(data => {
            setRealData(data);
          });
          const newConsultationTypes = calculateConsultationTypes();
          setConsultationTypes(newConsultationTypes);
          loadRecentCases();
        }
      );

      // Real-time recent cases updates
      const recentCasesChannel = realtimeChatService.subscribeToRecentCaseUpdates(
        user.id,
        (payload) => {
          console.log('📁 Real-time recent case update received:', payload);
          loadRecentCases();
        }
      );

      // Real-time wallet updates
      const walletChannel = realtimeChatService.subscribeToWalletUpdates(
        user.id,
        (payload) => {
          console.log('💰 Real-time wallet update received:', payload);
          loadWalletData();
        }
      );

      // Cleanup subscriptions on unmount
      return () => {
        realtimeChatService.unsubscribe(availabilityChannel);
        realtimeChatService.unsubscribe(metricsChannel);
        realtimeChatService.unsubscribe(bookingNotificationChannel);
        realtimeChatService.unsubscribe(caseStatusChannel);
        realtimeChatService.unsubscribe(caseActivityChannel);
        // Cleanup new subscriptions
        realtimeChatService.unsubscribe(earningsChannel);
        realtimeChatService.unsubscribe(clientsChannel);
        realtimeChatService.unsubscribe(pendingCasesChannel);
        realtimeChatService.unsubscribe(messagesChannel);
        realtimeChatService.unsubscribe(consultationTypesChannel);
        realtimeChatService.unsubscribe(appointmentsChannel);
        realtimeChatService.unsubscribe(performanceChannel);
        realtimeChatService.unsubscribe(recentCasesChannel);
        realtimeChatService.unsubscribe(walletChannel);
      };
    }
  }, [user?.id]);

  // Update real data when wallet data changes
  useEffect(() => {
    if (walletData.availableBalance > 0) {
      console.log('💰 Wallet data updated, recalculating dashboard metrics');
      const data = calculateRealData();
      setRealData(data);
    }
  }, [walletData.availableBalance]);

  // Update real data when dashboard metrics change (this is crucial!)
  useEffect(() => {
    if (dashboardMetrics?.availableBalance) {
      const data = calculateRealData();
      setRealData(data);
    }
  }, [dashboardMetrics?.availableBalance]);

  // Simple function to sync wallet balance directly
  const syncWalletBalance = async () => {
    if (!user?.id) return;
    
    try {
      // Try to get wallet data from localStorage first
      const walletData = JSON.parse(localStorage.getItem('lawyer_wallet') || '{}');
      if (walletData.availableBalance) {
        const balance = parseFloat(walletData.availableBalance) || 0;
        console.log('💰 Direct wallet sync from localStorage:', balance);
        
        setRealData(prev => ({
          ...prev,
          availableBalance: balance
        }));
        
        setWalletData(prev => ({
          ...prev,
          availableBalance: balance
        }));
        
        return;
      }
      
      // If not in localStorage, try wallet service
      const walletService = await import('../services/walletService');
      console.log('💰 Direct wallet sync - service imported:', walletService);
      
      const result = await walletService.default.getLawyerWallet(user.id);
      console.log('💰 Direct wallet sync - service call result:', result);
      
      if (result.success && result.data.availableBalance) {
        const balance = parseFloat(result.data.availableBalance) || 0;
        console.log('💰 Direct wallet sync from service:', balance);
        
        setRealData(prev => ({
          ...prev,
          availableBalance: balance
        }));
        
        setWalletData(prev => ({
          ...prev,
          availableBalance: balance
        }));
      }
    } catch (error) {
      console.error('Error in direct wallet sync:', error);
    }
  };

  // Initialize sample appointments for testing
  useEffect(() => {
    // Clear any corrupted data first
    try {
      const lawyerCases = JSON.parse(localStorage.getItem('lawyer_cases') || '[]');
      if (!Array.isArray(lawyerCases)) {
        console.log('Clearing corrupted lawyer_cases data');
        localStorage.removeItem('lawyer_cases');
      }
    } catch (error) {
      console.log('Clearing corrupted lawyer_cases data due to parse error');
      localStorage.removeItem('lawyer_cases');
    }

    // REMOVED: Sample appointments initialization - now using real data only
    // const existingAppointments = JSON.parse(localStorage.getItem('lawyer_appointments') || '[]');
    // if (existingAppointments.length === 0) {
    //   const sampleAppointments = [
    //     {
    //       id: 1,
    //       clientName: 'Emily Davis',
    //       date: '15/12/2024',
    //       time: '10:00 AM',
    //       type: 'Video Call',
    //       status: 'scheduled',
    //       caseType: 'Corporate Merger Consultation',
    //       amount: '₹300'
    //     },
    //     {
    //       id: 2,
    //       clientName: 'Robert Wilson',
    //       date: '16/12/2024',
    //       time: '2:00 PM',
    //       type: 'Phone Call',
    //       status: 'scheduled',
    //       caseType: 'Employment Contract Review',
    //       amount: '₹200'
    //     },
    //     {
    //       id: 3,
    //       clientName: 'Sarah Johnson',
    //       date: '17/12/2024',
    //       time: '11:30 AM',
    //       type: 'In Person',
    //       status: 'scheduled',
    //       caseType: 'Business Contract Review',
    //       amount: '₹450'
    //     }
    //   ];
    //   localStorage.setItem('lawyer_appointments', JSON.stringify(sampleAppointments));
    // }

    // REMOVED: Sample chat data initialization - now using real data only
    // const existingChats = JSON.parse(localStorage.getItem('lawyer_chats') || '[]');
    // if (existingChats.length === 0) {
    //   const sampleChats = [
    //     {
    //       id: 1,
    //       clientName: 'Emily Davis',
    //       lastMessage: 'Thank you for the consultation',
    //       unreadCount: 3,
    //       timestamp: '2 hours ago',
    //       status: 'active'
    //     },
    //     {
    //       id: 2,
    //       clientName: 'Robert Wilson',
    //       lastMessage: 'When can we schedule the next meeting?',
    //       unreadCount: 1,
    //       timestamp: '1 day ago',
    //       status: 'active'
    //     },
    //     {
    //       id: 3,
    //       clientName: 'Sarah Johnson',
    //       lastMessage: 'Documents have been sent',
    //       unreadCount: 0,
    //       timestamp: '2 days ago',
    //       status: 'completed'
    //     }
    //   ];
    //   localStorage.setItem('lawyer_chats', JSON.stringify(sampleChats));
    // }

    // Initialize empty lawyer_cases array if it doesn't exist
    const existingCases = JSON.parse(localStorage.getItem('lawyer_cases') || '[]');
    if (!Array.isArray(existingCases)) {
      localStorage.setItem('lawyer_cases', JSON.stringify([]));
    }
    
    // Calculate and set real data immediately
    const calculatedData = calculateRealData();
    const consultationTypesData = calculateConsultationTypes();
    const recentCasesData = getRecentCases();
    
    console.log('Calculated data:', calculatedData); // Debug log
    console.log('Consultation types:', consultationTypesData); // Debug log
    console.log('Recent cases:', recentCasesData); // Debug log
    
    setRealData(calculatedData);
    setConsultationTypes(consultationTypesData);
    setRecentCases(recentCasesData);
  }, []);

  // Recalculate data when localStorage changes (for real-time updates)
  useEffect(() => {
    const handleStorageChange = () => {
      const calculatedData = calculateRealData();
      const consultationTypesData = calculateConsultationTypes();
      const recentCasesData = getRecentCases();
      
      console.log('Storage changed, new data:', calculatedData); // Debug log
      console.log('Storage changed, consultation types:', consultationTypesData); // Debug log
      console.log('Storage changed, recent cases:', recentCasesData); // Debug log
      
      setRealData(calculatedData);
      setConsultationTypes(consultationTypesData);
      setRecentCases(recentCasesData);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Mock data for the dashboard - now using real calculated data
  const stats = [
    {
      title: t.totalClients,
      value: realData.totalClients.toString(),
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
    },
    {
      title: t.totalEarnings,
      value: `₹${realData.totalEarnings.toLocaleString()}`,
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-50 dark:bg-green-900/20'
    },
    {
      title: t.pendingCases,
      value: realData.pendingCases.toString(),
      icon: FileText,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20'
    },
    {
      title: t.messages,
      value: realData.totalMessages.toString(),
      icon: MessageSquare,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20'
    }
  ];

  const quickActions = [
    {
      title: t.rescheduleAppointment,
      icon: Calendar,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      action: () => navigate('/lawyer-reschedule-appointment')
    },
    {
      title: t.viewCases,
      icon: FileText,
      color: 'text-green-500',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      action: () => navigate('/lawyer-cases')
    },
    {
      title: t.manageProfile,
      icon: Users,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      action: () => navigate('/lawyer-profile-settings')
    },
    {
      title: 'Your Ratings/Reviews',
      icon: Star,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      action: () => navigate('/lawyer-ratings-reviews')
    }
  ];

  const handleRescheduleAppointment = () => {
    // Get existing appointments from localStorage
    const existingAppointments = JSON.parse(localStorage.getItem('lawyer_appointments') || '[]');
    
    if (existingAppointments.length === 0) {
      alert('No appointments found to reschedule. Please schedule an appointment first.');
      return;
    }

    // Show appointment selection modal
    const appointmentOptions = existingAppointments.map((apt, index) => 
      `${index + 1}. ${apt.clientName} - ${apt.date} at ${apt.time} (${apt.type})`
    ).join('\n');

    const selectedIndex = prompt(
      `Select an appointment to reschedule:\n\n${appointmentOptions}\n\nEnter the number (1-${existingAppointments.length}):`
    );

    if (!selectedIndex || isNaN(selectedIndex) || selectedIndex < 1 || selectedIndex > existingAppointments.length) {
      alert('Invalid selection. Please try again.');
      return;
    }

    const appointmentIndex = parseInt(selectedIndex) - 1;
    const selectedAppointment = existingAppointments[appointmentIndex];

    // Get new date and time
    const newDate = prompt('Enter new date (DD/MM/YYYY):', selectedAppointment.date);
    if (!newDate) return;

    const newTime = prompt('Enter new time (HH:MM AM/PM):', selectedAppointment.time);
    if (!newTime) return;

    // Update the appointment
    const updatedAppointments = [...existingAppointments];
    updatedAppointments[appointmentIndex] = {
      ...selectedAppointment,
      date: newDate,
      time: newTime,
      status: 'rescheduled',
      originalDate: selectedAppointment.date,
      originalTime: selectedAppointment.time,
      rescheduledAt: new Date().toISOString()
    };

    // Save back to localStorage
    localStorage.setItem('lawyer_appointments', JSON.stringify(updatedAppointments));

    // Show confirmation
    alert(`Appointment rescheduled successfully!\n\nClient: ${selectedAppointment.clientName}\nNew Date: ${newDate}\nNew Time: ${newTime}\n\nA notification will be sent to the client.`);

    // Simulate sending notification to client
    setTimeout(() => {
      console.log(`Notification sent to ${selectedAppointment.clientName}: Your appointment has been rescheduled to ${newDate} at ${newTime}`);
    }, 1000);
  };

  const handleClearStorage = () => {
    // Clear all dummy data
    const keysToClear = [
      'lawyer_appointments',
      'lawyer_cases', 
      'lawyer_chats',
      'user_data',
      'lawyer_scheduled_offline',
      'client_appointments',
      'client_cases',
      'client_chats',
      'sample_data',
      'dummy_data'
    ];

    keysToClear.forEach(key => {
      localStorage.removeItem(key);
    });

    // Also clear any keys containing 'dummy' or 'sample'
    Object.keys(localStorage).forEach(key => {
      if (key.toLowerCase().includes('dummy') || key.toLowerCase().includes('sample')) {
        localStorage.removeItem(key);
      }
    });

    clearStorageSpace();
    toast.success('All dummy data cleared! Refreshing page...');
    
    // Refresh the page after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  // Online/Offline status functions
  const handleStatusToggle = async () => {
    if (isOnline) {
      setShowOfflineModal(true);
    } else {
      // Go back online
      if (!user?.id) return;
      
      try {
        const result = await lawyerService.updateAvailabilityStatus(user.id, 'online');
        if (result.success) {
          setIsOnline(true);
          toast.success('You are now online');
        } else {
          toast.error('Failed to update availability status');
        }
      } catch (error) {
        console.error('Error updating availability status:', error);
        toast.error('Failed to update availability status');
      }
    }
  };

  // Load current availability status
  const loadCurrentAvailabilityStatus = async () => {
    if (!user?.id) return;
    
    try {
      const result = await lawyerService.getCurrentAvailabilityStatus(user.id);
      if (result.success) {
        const { availability_status, offline_reason, return_time } = result.data;
        setIsOnline(availability_status === 'online');
        setOfflineReason(offline_reason || '');
      }
    } catch (error) {
      console.error('Error loading availability status:', error);
    }
  };

  const handleGoOfflineNow = () => {
    setShowOfflineModal(false);
    setShowReasonModal(true);
  };

  const handleScheduleTimeOff = () => {
    setShowOfflineModal(false);
    setShowScheduleReasonModal(true);
  };

  const handleCancelOffline = () => {
    setShowOfflineModal(false);
    setOfflineReason('');
  };

  const handleReasonSelection = async (reason) => {
    setOfflineReason(reason);
    setShowReasonModal(false);
    setShowReturnTimeModal(true);
  };

  const handleReturnTimeSelection = async (timeOption) => {
    setSelectedReturnTime(timeOption);
    
    if (timeOption === 'custom') {
      setShowCustomDateTimePicker(true);
    } else {
      // Calculate return time based on selection
      const now = new Date();
      let returnTime;
      
      switch (timeOption) {
        case '30min':
          returnTime = new Date(now.getTime() + 30 * 60 * 1000);
          break;
        case '1hr':
          returnTime = new Date(now.getTime() + 60 * 60 * 1000);
          break;
        case '2hr':
          returnTime = new Date(now.getTime() + 2 * 60 * 60 * 1000);
          break;
        case 'tomorrow':
          returnTime = new Date(now.getTime() + 24 * 60 * 60 * 1000);
          break;
        case 'untilTurnOn':
          returnTime = null; // No specific time
          break;
        default:
          returnTime = now;
      }
      
      // Go offline with return time
      await goOfflineWithReturnTime(returnTime, timeOption);
    }
  };

  const handleCustomDateTimeConfirm = async () => {
    if (customReturnDateTime) {
      const returnTime = new Date(customReturnDateTime);
      await goOfflineWithReturnTime(returnTime, 'custom');
    }
  };

  const goOfflineWithReturnTime = async (returnTime, timeOption) => {
    if (!user?.id) return;
    
    try {
      const result = await lawyerService.updateAvailabilityStatus(
        user.id, 
        'offline', 
        offlineReason, 
        returnTime
      );
      
      if (result.success) {
        setIsOnline(false);
        setShowReturnTimeModal(false);
        setShowCustomDateTimePicker(false);
        setSelectedReturnTime('');
        setCustomReturnDateTime('');
        
        toast.success('You are now offline');
        
        // Reset states
        setOfflineReason('');
      } else {
        toast.error('Failed to update availability status');
      }
    } catch (error) {
      console.error('Error updating availability status:', error);
      toast.error('Failed to update availability status');
    }
  };

  const handleBackToOfflineModal = () => {
    setShowReturnTimeModal(false);
    setShowOfflineModal(true);
  };

  const handleBackToReasonModal = () => {
    setShowReturnTimeModal(false);
    setShowReasonModal(true);
  };

  const handleBackToReturnTimeModal = () => {
    setShowCustomDateTimePicker(false);
    setShowReturnTimeModal(true);
  };

  // Schedule Time Off functions
  const handleScheduleReasonSelection = (reason) => {
    setScheduleReason(reason);
    setShowScheduleReasonModal(false);
    setShowScheduleDateTimeModal(true);
  };

  const handleScheduleDateTimeConfirm = () => {
    if (scheduleStartDate && scheduleStartTime && scheduleEndDate && scheduleEndTime) {
      // Save scheduled offline time
      const scheduledOffline = {
        reason: scheduleReason,
        startDate: scheduleStartDate,
        startTime: scheduleStartTime,
        endDate: scheduleEndDate,
        endTime: scheduleEndTime,
        scheduledAt: new Date().toISOString()
      };
      
      // Save to localStorage
      const existingScheduled = JSON.parse(localStorage.getItem('lawyer_scheduled_offline') || '[]');
      existingScheduled.push(scheduledOffline);
      localStorage.setItem('lawyer_scheduled_offline', JSON.stringify(existingScheduled));
      
      // Reset states
      setShowScheduleDateTimeModal(false);
      setScheduleReason('');
      setScheduleStartDate('');
      setScheduleStartTime('');
      setScheduleEndDate('');
      setScheduleEndTime('');
      
      // Show confirmation
      alert(`Scheduled offline successfully!\n\nReason: ${scheduleReason}\nFrom: ${scheduleStartDate} ${scheduleStartTime}\nTo: ${scheduleEndDate} ${scheduleEndTime}`);
    }
  };

  const handleBackToScheduleReason = () => {
    setShowScheduleDateTimeModal(false);
    setShowScheduleReasonModal(true);
  };

  const handleBackToScheduleOfflineModal = () => {
    setShowScheduleReasonModal(false);
    setShowOfflineModal(true);
  };



  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header with Logo and Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  NyaySahaya
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Lawyer Dashboard
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Online/Offline Status Button */}
              <button
                onClick={handleStatusToggle}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                  isOnline 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                }`}
              >
                {isOnline ? (
                  <Wifi className="w-4 h-4" />
                ) : (
                  <WifiOff className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">
                  {isOnline ? t.online : t.offline}
                </span>
              </button>
              
              <button
                onClick={() => navigate('/lawyer-settings')}
                className="w-10 h-10 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200"
              >
                <Settings className="w-5 h-5 text-purple-500" />
              </button>
            </div>
          </div>
        </motion.div>

                 {/* Welcome Card */}
         <motion.div
           initial={{ opacity: 0, y: 20 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.6, delay: 0.1 }}
           className="mb-6"
         >
           <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
             <h2 className="text-2xl font-bold mb-1">
               {t.welcome} {lawyerName}
             </h2>
             <p className="text-purple-100 mb-4">
               {t.toNyaySahaya}
             </p>
             
             

             
             <motion.button
               onClick={() => navigate('/lawyer-wallet')}
               className="w-full flex items-center gap-2 p-3 bg-white/10 rounded-xl hover:bg-white/20 transition-all duration-200"
               whileHover={{ scale: 1.02 }}
               whileTap={{ scale: 0.98 }}
             >
               <DollarSign className="w-5 h-5" />
               <span className="text-xl font-bold">
                 {t.availableBalance}: ₹{realData.availableBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
               </span>
               <div className="ml-auto">
                 <div className="w-2 h-2 border-r-2 border-t-2 border-white transform rotate-45" />
               </div>
             </motion.button>
           </div>
         </motion.div>

        {/* Real-time Lawyer Dashboard Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6"
        >
          <LawyerDashboardMetrics />
        </motion.div>



        {/* Pending Appointments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mb-6"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Pending Appointments
              </h3>
              {loadingPending && (
                <div className="flex items-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Loading...
                </div>
              )}
            </div>
            
            {pendingAppointments.length > 0 ? (
              <div className="space-y-3">
                {pendingAppointments.map((appointment) => (
                  <div key={appointment.id} className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                            {appointment.is_emergency ? 'Emergency Consultation' : 'Regular Consultation'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 mb-2">
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {appointment.user_profiles?.full_name || 'Client Name'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {new Date(appointment.appointment_date).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {appointment.start_time}
                            </span>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                            {appointment.subject}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {appointment.description}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            ₹{appointment.amount}
                          </span>
                          <span className="text-xs text-gray-500">
                            {appointment.duration_minutes} minutes
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2 ml-4">
                        <button
                          onClick={() => handleAcceptAppointment(appointment.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg transition-colors"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Accept
                        </button>
                        <button
                          onClick={() => handleRejectAppointment(appointment.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400">No pending appointments</p>
                <p className="text-sm text-gray-400 dark:text-gray-500">All appointments have been processed</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Cases */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-6"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Recent Cases
            </h3>
            <div className="space-y-3">
              {recentCases.length > 0 ? (
                recentCases.map((caseItem, index) => {
                  const getStatusColor = (status) => {
                    switch (status) {
                      case 'completed':
                        return 'bg-green-500';
                      case 'pending':
                        return 'bg-orange-500';
                      case 'scheduled':
                        return 'bg-blue-500';
                      case 'rescheduled':
                        return 'bg-purple-500';
                      default:
                        return 'bg-gray-500';
                    }
                  };

                  const getStatusText = (status) => {
                    switch (status) {
                      case 'completed':
                        return 'Completed';
                      case 'pending':
                        return 'Pending';
                      case 'scheduled':
                        return 'Scheduled';
                      case 'rescheduled':
                        return 'Rescheduled';
                      default:
                        return status || 'Unknown';
                    }
                  };

                  const getTimeDisplay = (caseItem) => {
                    if (caseItem.time) return caseItem.time;
                    if (caseItem.dateTime) {
                      // Extract time from dateTime if it exists
                      const dateTime = new Date(caseItem.dateTime);
                      if (!isNaN(dateTime.getTime())) {
                        return dateTime.toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        });
                      }
                    }
                    return getStatusText(caseItem.status);
                  };

                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 ${getStatusColor(caseItem.status)} rounded-full`}></div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{caseItem.clientName}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{caseItem.caseType || caseItem.type || 'Consultation'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900 dark:text-white">{caseItem.amount || '₹0'}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{getTimeDisplay(caseItem)}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">No recent cases found</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-6"
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              {t.quickActions}
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {quickActions.map((action, index) => (
                <motion.button
                  key={action.title}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={action.action}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${action.bgColor}`}>
                    <action.icon className={`w-5 h-5 ${action.color}`} />
                  </div>
                  <span className="text-xs font-medium text-gray-900 dark:text-white text-center">
                    {action.title}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Offline Confirmation Modal */}
        {showOfflineModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t.activeStatus}
                </h3>
                <button
                  onClick={handleCancelOffline}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {t.offlineConfirmation}
              </p>
              
                                            <div className="space-y-3">
                 <button
                   onClick={handleGoOfflineNow}
                   className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                 >
                   <WifiOff className="w-4 h-4" />
                   {t.goOfflineNow}
                 </button>
                 
                 <button
                   onClick={handleScheduleTimeOff}
                   className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                 >
                   <CalendarIcon className="w-4 h-4" />
                   {t.scheduleTimeOff}
                 </button>
               
                 <button
                   onClick={handleCancelOffline}
                   className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                 >
                   {t.cancel}
                 </button>
               </div>
            </motion.div>
          </div>
        )}

        {/* Reason Selection Modal */}
        {showReasonModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t.reasonForOffline}
                </h3>
                <button
                  onClick={() => {
                    setShowReasonModal(false);
                    setShowOfflineModal(true);
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
                             <div className="space-y-3 mb-6">
                 <button
                   onClick={() => handleReasonSelection('incorrect_availability')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <Clock className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.incorrectAvailability}</span>
                 </button>
                 
                 <button
                   onClick={() => handleReasonSelection('temporarily_close')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <Building className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.temporarilyClose}</span>
                 </button>
               </div>
              
                             <button
                 onClick={() => {
                   setShowReasonModal(false);
                   setShowOfflineModal(true);
                 }}
                 className="w-full bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
               >
                 {t.back}
               </button>
            </motion.div>
          </div>
        )}

        {/* Return Time Selection Modal */}
        {showReturnTimeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t.whenWillYouReturn}
                </h3>
                                 <button
                   onClick={handleBackToReasonModal}
                   className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                 >
                   <X className="w-5 h-5" />
                 </button>
              </div>
              
                             <div className="space-y-3 mb-6">
                 <button
                   onClick={() => handleReturnTimeSelection('30min')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <Clock className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.thirtyMinutes}</span>
                 </button>
                 
                 <button
                   onClick={() => handleReturnTimeSelection('1hr')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <Clock className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.oneHour}</span>
                 </button>
                 
                 <button
                   onClick={() => handleReturnTimeSelection('2hr')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <Clock className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.twoHours}</span>
                 </button>
                 
                 <button
                   onClick={() => handleReturnTimeSelection('untilTurnOn')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <Wifi className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.untilTurnOn}</span>
                 </button>
                 
                 <button
                   onClick={() => handleReturnTimeSelection('tomorrow')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <Calendar className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.tomorrow}</span>
                 </button>
                 
                 <button
                   onClick={() => handleReturnTimeSelection('custom')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <CalendarIcon className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.specifyDateTime}</span>
                 </button>
               </div>
              
                                                           <button
                 onClick={handleBackToReasonModal}
                 className="w-full bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
               >
                 {t.back}
               </button>
            </motion.div>
          </div>
        )}

        {/* Custom Date Time Picker Modal */}
        {showCustomDateTimePicker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t.selectDateTime}
                </h3>
                <button
                  onClick={handleBackToReturnTimeModal}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.selectDateTime}
                </label>
                                 <input
                   type="datetime-local"
                   value={customReturnDateTime}
                   onChange={(e) => setCustomReturnDateTime(e.target.value)}
                   className="w-full p-3 border border-purple-300 dark:border-purple-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                   min={new Date().toISOString().slice(0, 16)}
                 />
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={handleCustomDateTimeConfirm}
                  disabled={!customReturnDateTime}
                  className={`w-full font-semibold py-3 px-4 rounded-lg transition-colors duration-200 ${
                    customReturnDateTime
                      ? 'bg-purple-500 hover:bg-purple-600 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {t.confirm}
                </button>
                
                                 <button
                   onClick={handleBackToReturnTimeModal}
                   className="w-full bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                 >
                   {t.back}
                 </button>
              </div>
            </motion.div>
          </div>
        )}

                 {/* Schedule Reason Selection Modal */}
         {showScheduleReasonModal && (
           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
             <motion.div
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4"
             >
               <div className="flex items-center justify-between mb-4">
                 <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                   {t.reasonForScheduling}
                 </h3>
                                   <button
                    onClick={handleBackToScheduleOfflineModal}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <X className="w-5 h-5" />
                  </button>
               </div>
               
               <div className="space-y-3 mb-6">
                 <button
                   onClick={() => handleScheduleReasonSelection('closed_due_to_festival')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <Calendar className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.closedDueToFestival}</span>
                 </button>
                 
                 <button
                   onClick={() => handleScheduleReasonSelection('permanently_shut')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <Building className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.permanentlyShut}</span>
                 </button>
                 
                 <button
                   onClick={() => handleScheduleReasonSelection('going_out_of_station')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <MapPin className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.goingOutOfStation}</span>
                 </button>
                 
                 <button
                   onClick={() => handleScheduleReasonSelection('others')}
                   className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3"
                 >
                   <MoreHorizontal className="w-4 h-4 text-gray-500" />
                   <span className="text-gray-900 dark:text-white font-medium">{t.others}</span>
                 </button>
               </div>
               
                                                               <button
                   onClick={handleBackToScheduleOfflineModal}
                   className="w-full bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                 >
                   {t.back}
                 </button>
             </motion.div>
           </div>
         )}

         {/* Schedule Date Time Modal */}
         {showScheduleDateTimeModal && (
           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
             <motion.div
               initial={{ opacity: 0, scale: 0.9 }}
               animate={{ opacity: 1, scale: 1 }}
               className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4"
             >
               <div className="flex items-center justify-between mb-4">
                 <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                   {t.scheduleTimeOff}
                 </h3>
                 <button
                   onClick={handleBackToScheduleReason}
                   className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                 >
                   <X className="w-5 h-5" />
                 </button>
               </div>
               
               <div className="space-y-4 mb-6">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     {t.startDate}
                   </label>
                                       <input
                      type="date"
                      value={scheduleStartDate}
                      onChange={(e) => setScheduleStartDate(e.target.value)}
                      className="w-full p-3 border border-purple-300 dark:border-purple-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min={new Date().toISOString().split('T')[0]}
                    />
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     {t.startTime}
                   </label>
                                       <input
                      type="time"
                      value={scheduleStartTime}
                      onChange={(e) => setScheduleStartTime(e.target.value)}
                      className="w-full p-3 border border-purple-300 dark:border-purple-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     {t.endDate}
                   </label>
                                       <input
                      type="date"
                      value={scheduleEndDate}
                      onChange={(e) => setScheduleEndDate(e.target.value)}
                      className="w-full p-3 border border-purple-300 dark:border-purple-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min={scheduleStartDate || new Date().toISOString().split('T')[0]}
                    />
                 </div>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     {t.endTime}
                   </label>
                                       <input
                      type="time"
                      value={scheduleEndTime}
                      onChange={(e) => setScheduleEndTime(e.target.value)}
                      className="w-full p-3 border border-purple-300 dark:border-purple-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                 </div>
               </div>
               
               <div className="space-y-3">
                 <button
                   onClick={handleScheduleDateTimeConfirm}
                   disabled={!scheduleStartDate || !scheduleStartTime || !scheduleEndDate || !scheduleEndTime}
                   className={`w-full font-semibold py-3 px-4 rounded-lg transition-colors duration-200 ${
                     scheduleStartDate && scheduleStartTime && scheduleEndDate && scheduleEndTime
                       ? 'bg-purple-500 hover:bg-purple-600 text-white'
                       : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                   }`}
                 >
                   {t.scheduleOffline}
                 </button>
                 
                                   <button
                    onClick={handleBackToScheduleReason}
                    className="w-full bg-purple-100 hover:bg-purple-200 text-purple-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                  >
                    {t.back}
                  </button>
               </div>
             </motion.div>
           </div>
         )}
      </div>
    );
  };

export default LawyerDashboard;
